import React, { Fragment, memo, useMemo } from "react";
import { Col, Form, Select } from "antd";
import * as texts from "../locales";
import { WrappedFormUtils } from "antd/lib/form/Form";
import { useFreire } from "../../../utils/freireContext";
import { sorter } from "../../../utils/sorter";
import { useOnMount, useOnUpdate } from "../../../hooks";

const { Option } = Select;

interface IBrasilSelectLocation {
  form: WrappedFormUtils<any>;
  disabled: boolean;
  required: boolean;
  loadingStates: boolean;
  loadingCities: boolean;
  states: any[];
  cities: any[];
  onStateChange: (stateId: string) => void;
}

const BrasilSelectLocation: React.FC<IBrasilSelectLocation> = ({
  form,
  disabled,
  required,
  loadingStates,
  loadingCities,
  states,
  cities,
  onStateChange,
}) => {
  const { freire } = useFreire();

  const requiredFieldRule = {
    required,
    message: freire(texts.REQUIRED_FIELD),
  };

  const selectedState = form.getFieldValue("address2.state");

  const getInitialCities = () => {
    if (cities.length === 0 && selectedState) {
      onStateChange(selectedState);
    }
  };

  useOnMount(getInitialCities);

  useOnUpdate(() => {
    if (selectedState) {
      onStateChange(selectedState);
    }
  }, [selectedState]);

  const onSelectState = () => form.resetFields(["address2.city"]);

  const statesSorted: any[] = useMemo(() => sorter(states, "nome"), [states]);
  const citiesSorted: any[] = useMemo(() => sorter(cities, "nome"), [cities]);

  return (
    <Fragment>
      <Col span={12}>
        <Form.Item label={freire(texts.STATE)}>
          {form.getFieldDecorator("address2.state", {
            rules: [requiredFieldRule],
          })(
            <Select
              showSearch
              placeholder={
                loadingStates
                  ? freire(texts.LOADING)
                  : freire(texts.SELECT_STATE)
              }
              onSelect={onSelectState}
              loading={loadingStates}
              disabled={loadingStates || disabled}
              filterOption={(input: any, option: any) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {statesSorted.map((state) => (
                <Option key={`${state.id}-${state.nome}`} value={state.sigla}>
                  {state.nome}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item label={freire(texts.CITY)}>
          {form.getFieldDecorator("address2.city", {
            rules: [requiredFieldRule],
          })(
            <Select
              showSearch
              placeholder={
                loadingCities
                  ? freire(texts.LOADING)
                  : freire(texts.SELECT_CITY)
              }
              loading={loadingCities}
              disabled={!selectedState || loadingCities || disabled}
            >
              {citiesSorted.map((city) => (
                <Option key={city.id} value={city.nome}>
                  {city.nome}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Col>
    </Fragment>
  );
};

export default memo(BrasilSelectLocation);
